import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles } from './DiagnosticRevisionTable.style';
import * as actions from '../../../../../store';
import { DndContext } from '@dnd-kit/core';
import SectionPerBreast from './components/SectionPerBreast';
import { useDragHandlers } from './hooks/useDragHandlers';
import { useFindingsFilter } from './hooks/useFindingsFilter';
import { useHoveredClass } from './hooks/useHoveredClass';
import { setupResizeObserverErrorHandling } from './utils/errorHandling';
import { buildTableRows } from './helpers/dataProcessing';
import { useMissedFindings } from '../../../../../helpers/hooks/useMissedFindings';
import animation from '../../../../../resources/processing_animation.gif';
import { getHighestClass, handleFilteringLesionType } from './utils/utils';
import FilterControls from './components/FilterControls';
import {
  handleClassChange,
  handleTypeChange,
  handleUnmatch,
  deleteFinding,
  deleteAllFindings,
} from './helpers/tableActions';
import { useResponsive } from './hooks/useResponsive';

const DiagnosticRevisionTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Redux
  const customMicrocalc = useSelector((state) => state.labeling?.customMicrocalc);
  const customOpacities = useSelector((state) => state.labeling?.customOpacities);
  const currentExam = useSelector((state) => state.examinations?.currentExam);
  const resultsExamId = useSelector((state) => state.results?.exam_id);
  const savingPending = useSelector((state) => state.labeling?.savingPending);
  const justEditedRows = useSelector((state) => state.labeling?.justEditedRows);
  const draggingLesionId = useSelector((state) => state.labeling?.draggingLesionId);
  const exam_id = useSelector((state) => state.results?.exam_id);
  const projections = useSelector((state) => state.results?.projections) || {};
  const zoomedProjection = useSelector((state) => state.gui?.zoomedProjection);
  const { isVisible } = useMissedFindings();
  const { smallScreen } = useResponsive();

  const { rcc = [], lcc = [], rmlo = [], lmlo = [] } = projections;
  const rightProjections = rcc || rmlo;
  const leftProjections = lcc || lmlo;

  const rightTableBodyRef = useRef(null);
  const leftTableBodyRef = useRef(null);
  const stateRef = useRef({ customMicrocalc, customOpacities });

  // Local state
  const [draggingActive, setDraggingActive] = useState(false);
  const [rightTableBodyHeight, setRightTableBodyHeight] = useState(null);
  const [leftTableBodyHeight, setLeftTableBodyHeight] = useState(null);
  const [rightOrderBy, setRightOrderBy] = useState(null);
  const [rightOrder, setRightOrder] = useState('asc');
  const [leftOrderBy, setLeftOrderBy] = useState(null);
  const [leftOrder, setLeftOrder] = useState('asc');
  const [expandedClusters, setExpandedClusters] = useState({});
  const [pinnedRow, setPinnedRow] = useState(null);

  // Custom hooks
  const { handleDragStart, handleDragOver, handleDragEnd } = useDragHandlers(setDraggingActive);

  // Hovered class hook
  const { setHoveredClass, hoveredClass, hoveredProjections } = useHoveredClass();

  const labelingReady =
    customOpacities != null &&
    customMicrocalc != null &&
    currentExam &&
    resultsExamId &&
    String(currentExam) === String(resultsExamId);

  // Synchronous derivation to group findings by breast
  const { rightFindings, leftFindings } = useMemo(() => {
    if (!labelingReady) {
      return { rightFindings: [], leftFindings: [] };
    }
    return buildTableRows(customOpacities, customMicrocalc, zoomedProjection, isVisible);
  }, [labelingReady, customOpacities, customMicrocalc, zoomedProjection, isVisible]);

  const allFindings = useMemo(() => [...rightFindings, ...leftFindings], [rightFindings, leftFindings]);

  // Filtering hook
  const {
    typeValue,
    classValue,
    filteredRightFindings,
    filteredLeftFindings,
    hasActiveFilters,
    availableTypes,
    availableClasses,
    handleFilterTypeChange,
    handleFilterClassChange,
    handleResetFilters,
  } = useFindingsFilter(rightFindings, leftFindings, hoveredClass, hoveredProjections);

  // Effects
  useEffect(() => {
    const cleanup = setupResizeObserverErrorHandling();
    return cleanup;
  }, []);

  // Reset state when exam changes
  useEffect(() => {
    setExpandedClusters({});
    setPinnedRow(null);
    setHoveredClass(null, null);
    setRightOrderBy(null);
    setRightOrder('asc');
    setLeftOrderBy(null);
    setLeftOrder('asc');
  }, [exam_id, setExpandedClusters]);

  useEffect(() => {
    handleFilteringLesionType('opacities', dispatch, actions);
    handleFilteringLesionType('microcalc', dispatch, actions);
    dispatch(actions.setShowMissedFindings(true));
    setDraggingActive(false);
    dispatch(actions.resetHoverState());
  }, [exam_id]);

  // Refs for stable handler callbacks
  useEffect(() => {
    stateRef.current = { customMicrocalc, customOpacities };
  }, [customMicrocalc, customOpacities]);

  // Auto-expand the cluster that contains a just-edited finding
  useEffect(() => {
    if (!justEditedRows) return;

    const allFindings = [...rightFindings, ...leftFindings];
    for (const finding of allFindings) {
      const groupId = finding?.object_id;

      if (!groupId) continue;

      const editedId = justEditedRows;
      const belongsToGroup =
        finding?.finding_id === editedId ||
        finding.nested_items?.some((item) => item.finding_id === editedId) ||
        finding.matched_lesions?.some((item) => item.finding_id === editedId);

      if (belongsToGroup) {
        setExpandedClusters((prev) => ({ ...prev, [groupId]: true }));
        if (finding?.module_type === 'microcalc') {
          const projection = Array.isArray(finding?.projection)
            ? finding?.projection[0]
            : finding?.projection;
          dispatch(
            actions.changeMicrocalcToShow(finding?.finding_class, true, projection || finding?.breast),
          );
        }
        if (finding?.module_type === 'opacities') {
          dispatch(actions.changeOpacitiesToShow(finding?.finding_class, true));
        }
        break;
      }
    }
  }, [justEditedRows]);

  useEffect(() => {
    if (draggingActive) {
      document.body.style.overflowX = 'hidden';
    } else {
      document.body.style.overflowX = '';
    }
    return () => {
      document.body.style.overflowX = '';
    };
  }, [draggingActive]);

  // Measure and store table body heights
  useEffect(() => {
    if (!hoveredClass) {
      const measureHeights = () => {
        requestAnimationFrame(() => {
          if (rightTableBodyRef?.current) {
            const height = rightTableBodyRef?.current?.offsetHeight;
            if (height > 0) {
              setRightTableBodyHeight(height);
            }
          }

          // Measure left table body height
          if (leftTableBodyRef?.current) {
            const height = leftTableBodyRef?.current?.offsetHeight;
            if (height > 0) {
              setLeftTableBodyHeight(height);
            }
          }
        });
      };

      // Small delay to ensure nested items are rendered
      const timeoutId = setTimeout(measureHeights, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [rightFindings, leftFindings, expandedClusters, hoveredClass]);

  // Sort findings function - accepts orderBy and order as parameters
  const sortFindings = useCallback((findings, sortOrderBy, sortOrder) => {
    if (!sortOrderBy || !findings || findings.length === 0) {
      return findings;
    }

    const sorted = [...findings].sort((a, b) => {
      let aValue, bValue;

      switch (sortOrderBy) {
        case 'type':
          aValue = a.module_type || '';
          bValue = b.module_type || '';
          break;
        case 'class':
          // Sort by BI-RADS class: vessel, birads2, birads3, birads4, birads5, lesionKnown
          const classOrder = {
            vessel: 0,
            birads2: 1,
            birads3: 2,
            birads4: 3,
            birads5: 4,
            lesionKnown: 5,
          };
          aValue = classOrder[a.finding_class] ?? 999;
          bValue = classOrder[b.finding_class] ?? 999;
          break;
        case 'size':
          // Extract size area (width * height) for sorting
          const getSizeValue = (finding) => {
            // Check for matched_size first (string format)
            if (finding.matched_size) {
              if (Array.isArray(finding.matched_size)) {
                if (finding.matched_size.length >= 2) {
                  return (finding.matched_size[0] || 0) * (finding.matched_size[1] || 0);
                }
              } else if (typeof finding.matched_size === 'string') {
                const match = finding.matched_size.match(/([\d.]+)\s*x\s*([\d.]+)/);
                if (match) {
                  const width = parseFloat(match[1]) || 0;
                  const height = parseFloat(match[2]) || 0;
                  return width * height;
                }
              }
            }
            // Check for size (array format)
            if (finding.size && Array.isArray(finding.size) && finding.size.length >= 2) {
              return (finding.size[0] || 0) * (finding.size[1] || 0);
            }
            return 0;
          };
          aValue = getSizeValue(a);
          bValue = getSizeValue(b);
          break;
        case 'location':
          // Sort by location string, then by depth (prob improvable)
          aValue = `${a.location || ''}_${a.depth || ''}`;
          bValue = `${b.location || ''}_${b.depth || ''}`;
          break;
        case 'trend':
          // Sort by number of priors (history items.. not sure if this is needed)
          aValue = Array.isArray(a.priors) ? a.priors.length : 0;
          bValue = Array.isArray(b.priors) ? b.priors.length : 0;
          break;
        default:
          return 0;
      }

      // Handle comparison
      if (aValue < bValue) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, []);

  // Apply sorting to filtered findings - each table uses its own sorting state
  const sortedRightFindings = useMemo(() => {
    return sortFindings(filteredRightFindings, rightOrderBy, rightOrder);
  }, [filteredRightFindings, rightOrderBy, rightOrder, sortFindings]);

  const sortedLeftFindings = useMemo(() => {
    return sortFindings(filteredLeftFindings, leftOrderBy, leftOrder);
  }, [filteredLeftFindings, leftOrderBy, leftOrder, sortFindings]);

  // Table actions (class change, type change, unmatch, delete)
  const handleClassChangeForRow = useCallback(
    (lesionAllInfo, newClass) => {
      handleClassChange(
        exam_id,
        lesionAllInfo?.module_type,
        lesionAllInfo?.unique_position || lesionAllInfo?.matched_lesions,
        newClass,
        stateRef.current.customMicrocalc,
        stateRef.current.customOpacities,
        dispatch,
        actions,
        t,
      );
      dispatch(actions.resetHoverState());
    },
    [exam_id, dispatch, t],
  );

  const handleTypeChangeForRow = useCallback(
    async (lesionInfo, newType) => {
      await handleTypeChange(
        exam_id,
        lesionInfo?.module_type,
        lesionInfo?.unique_position || lesionInfo?.matched_lesions,
        newType,
        stateRef.current.customMicrocalc,
        stateRef.current.customOpacities,
        dispatch,
        actions,
        t,
      );
    },
    [exam_id],
  );

  const handleUnmatchForRow = useCallback(
    (lesionAllInfo) => {
      handleUnmatch(
        exam_id,
        lesionAllInfo?.unique_position || lesionAllInfo?.matched_lesions?.[0],
        stateRef.current.customMicrocalc,
        stateRef.current.customOpacities,
        dispatch,
        actions,
        t,
      );
    },
    [exam_id],
  );

  const handleDeleteForRow = useCallback(
    (lesionAllInfo, isGrouped, e) => {
      if (isGrouped || lesionAllInfo?.matched_lesions) {
        deleteAllFindings(
          lesionAllInfo?.matched_lesions || lesionAllInfo?.isMatchedMainRow
            ? 'delete_matched'
            : 'delete_class',
          exam_id,
          lesionAllInfo?.module_type,
          lesionAllInfo,
          stateRef.current.customMicrocalc,
          stateRef.current.customOpacities,
          dispatch,
          actions,
          t,
          e,
        );
      } else {
        deleteFinding(
          exam_id,
          lesionAllInfo?.module_type,
          lesionAllInfo?.unique_position,
          stateRef.current.customMicrocalc,
          stateRef.current.customOpacities,
          dispatch,
          actions,
          t,
          e,
        );
      }
    },
    [exam_id, dispatch, t],
  );

  const handleRowEnter = useCallback(
    ({ module_type, finding_class, projection, lesionAllInfo, isClustered, nested }) => {
      // Microcalc auto-show logic
      if (
        module_type === 'microcalc' &&
        !isClustered &&
        ['vessel', 'birads2', 'birads3'].includes(finding_class)
      ) {
        const normalizedProjection = Array.isArray(projection) ? projection[0] : projection;
        dispatch(actions.changeMicrocalcToShow(finding_class, true, normalizedProjection));
      }

      setHoveredClass(null, null);
      const isMatchedMainRow = !!lesionAllInfo?.isMatchedMainRow;
      const isGrouped = isClustered || isMatchedMainRow;
      const groupedObjectId = isGrouped ? lesionAllInfo?.object_id || null : null;
      const nextHover = {
        findingID: nested || !isGrouped ? lesionAllInfo?.finding_id || null : null,
        lesionID: groupedObjectId,
      };

      dispatch(actions.setSingleBoxHover(nextHover));
      if (isClustered) {
        const breast = lesionAllInfo?.object_id?.split('_')[0];
        dispatch(actions.changeMicrocalcToShow(lesionAllInfo?.finding_class || finding_class, true, breast));
      }
    },

    [dispatch, setHoveredClass],
  );

  const handleRowLeave = useCallback(
    ({ module_type, finding_class, projection, lesionAllInfo, isClustered, nested }) => {
      const rowPinId = lesionAllInfo?.finding_id || lesionAllInfo?.object_id;
      if (!!pinnedRow && pinnedRow === rowPinId) return;
      if ((lesionAllInfo?.object_id && expandedClusters?.[lesionAllInfo?.object_id]) || nested) return;

      if (module_type === 'microcalc' && ['vessel', 'birads2', 'birads3'].includes(finding_class)) {
        const normalizedProjection = Array.isArray(projection) ? projection[0] : projection;
        const breast = lesionAllInfo?.object_id?.split('_')[0];
        dispatch(actions.changeMicrocalcToShow(finding_class, false, normalizedProjection || breast));
      } else {
        dispatch(actions.setTargetLesionId(null));
        dispatch(actions.setDraggingLesionId(null));
      }

      dispatch(actions.resetHoverState());
    },
    [expandedClusters, pinnedRow],
  );

  // Sorting handler for right/left columns
  const handleRequestSort = useCallback(
    (side, property) => {
      const isRight = side === 'right';
      const currentOrderBy = isRight ? rightOrderBy : leftOrderBy;
      const currentOrder = isRight ? rightOrder : leftOrder;
      const setOrderBy = isRight ? setRightOrderBy : setLeftOrderBy;
      const setOrder = isRight ? setRightOrder : setLeftOrder;

      if (currentOrderBy === property) {
        if (currentOrder === 'asc') {
          setOrder('desc');
        } else {
          setOrderBy(null);
          setOrder('asc');
        }
      } else {
        setOrderBy(property);
        setOrder('asc');
      }
    },
    [rightOrderBy, leftOrderBy, rightOrder, leftOrder],
  );

  const handleClearHoverCallback = useCallback(
    (cls, projs) => {
      if (cls !== undefined) {
        setHoveredClass(cls, projs);
      } else {
        setHoveredClass(null, null);
      }
    },
    [setHoveredClass],
  );

  const handleClearSingleBoxHover = useCallback(() => {
    dispatch(actions.resetHoverState());
  }, []);

  const rowProps = useMemo(
    () => ({
      expandedClusters,
      setExpandedClusters,
      draggingLesionId,
      justEditedRows,
      exam_id,
      allFindings,
      onClassChange: handleClassChangeForRow,
      onTypeChange: handleTypeChangeForRow,
      onUnmatch: handleUnmatchForRow,
      onRowEnter: handleRowEnter,
      onRowLeave: handleRowLeave,
      onDelete: handleDeleteForRow,
      pinnedRow,
      setPinnedRow,
      smallScreen,
    }),
    [
      expandedClusters,
      draggingLesionId,
      justEditedRows,
      exam_id,
      allFindings,
      handleClassChangeForRow,
      handleTypeChangeForRow,
      handleUnmatchForRow,
      handleRowEnter,
      handleRowLeave,
      handleDeleteForRow,
      pinnedRow,
      smallScreen,
    ],
  );

  const sharedSectionProps = useMemo(
    () => ({
      zoomedProjection,
      hoveredClass,
      hoveredProjections,
      exam_id,
      typeValue,
      classValue,
      onClearHover: handleClearHoverCallback,
      onClearSingleBoxHover: handleClearSingleBoxHover,
      rowProps,
    }),
    [
      zoomedProjection,
      hoveredClass,
      hoveredProjections,
      exam_id,
      typeValue,
      classValue,
      handleClearHoverCallback,
      handleClearSingleBoxHover,
      rowProps,
    ],
  );

  const rightSectionProps = useMemo(
    () => ({
      title: t('diagnose.type_right_breast'),
      findingsPerBreast: sortedRightFindings,
      projections: ['rcc', 'rmlo'],
      hasActiveFilters: rightFindings.length > 0 && hasActiveFilters,
      findingsLength: rightFindings.length,
      highestClass: getHighestClass(rightFindings),
      empty: rightFindings.length === 0,
      noResults: rightProjections,
      tableBodyRef: rightTableBodyRef,
      storedHeight: rightTableBodyHeight,
      tableSide: 'right',
      orderBy: rightOrderBy,
      order: rightOrder,
      onRequestSort: (_, property) => handleRequestSort('right', property),
    }),
    [
      sortedRightFindings,
      rightFindings,
      hasActiveFilters,
      rightProjections,
      rightTableBodyHeight,
      rightOrderBy,
      rightOrder,
      handleRequestSort,
    ],
  );

  const leftSectionProps = useMemo(
    () => ({
      title: t('diagnose.type_left_breast'),
      findingsPerBreast: sortedLeftFindings,
      projections: ['lcc', 'lmlo'],
      hasActiveFilters: leftFindings.length > 0 && hasActiveFilters,
      findingsLength: leftFindings.length,
      highestClass: getHighestClass(leftFindings),
      empty: leftFindings.length === 0,
      noResults: leftProjections,
      tableBodyRef: leftTableBodyRef,
      storedHeight: leftTableBodyHeight,
      tableSide: 'left',
      orderBy: leftOrderBy,
      order: leftOrder,
      onRequestSort: (_, property) => handleRequestSort('left', property),
    }),
    [
      sortedLeftFindings,
      leftFindings,
      hasActiveFilters,
      leftProjections,
      leftTableBodyHeight,
      leftOrderBy,
      leftOrder,
      handleRequestSort,
    ],
  );

  return (
    <div className={`${classes.table_container}`}>
      {/* Saving loader */}
      {(!labelingReady || savingPending) && (
        <div className={classes.loading_overlay}>
          <img className={classes.loading_animation} src={animation} alt='loading_animation' />
        </div>
      )}

      {/* Filters controls */}
      <div className={classes.filter_controls_container}>
        {(rightFindings.length > 0 || leftFindings.length > 0) && (
          <FilterControls
            typeValue={typeValue}
            classValue={classValue}
            hasActiveFilters={hasActiveFilters}
            availableTypes={availableTypes}
            availableClasses={availableClasses}
            onTypeChange={handleFilterTypeChange}
            onClassChange={handleFilterClassChange}
            onReset={handleResetFilters}
            hoveredClass={hoveredClass}
          />
        )}
      </div>

      <Divider orientation='horizontal' style={{ margin: '8px 0' }} />

      {/* Diagnostics Revision Tables */}
      <>
        {/* Right Findings Table */}
        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragOver={handleDragOver}>
          <SectionPerBreast section={rightSectionProps} shared={sharedSectionProps} />
        </DndContext>

        {/* Left Findings Table */}
        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragOver={handleDragOver}>
          <SectionPerBreast section={leftSectionProps} shared={sharedSectionProps} />
        </DndContext>
      </>
    </div>
  );
};

export default memo(DiagnosticRevisionTable, (prevProps, nextProps) => true);
